import React, { useState } from 'react';
import { 
  BookOpen, Shield, Zap, Terminal, Activity, HelpCircle, 
  ChevronRight, AlertTriangle, Layers, Cpu, HeartHandshake,
  CheckCircle2, XCircle, BarChart3, Brain, BarChart2,
  Settings, User
} from 'lucide-react'; 
import { useTranslation } from 'react-i18next'; 

export const UserGuide = () => {
  const { t } = useTranslation(); 
  const [activeSection, setActiveSection] = useState('intro');

  const scrollTo = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="flex h-full gap-8 animate-in fade-in duration-500">
      
      {/* 1. НАВИГАЦИЯ (СЛЕВА) */}
      <div className="w-72 hidden xl:block shrink-0">
        <div className="fixed w-72 space-y-1 pr-4 overflow-y-auto h-[calc(100vh-100px)] no-scrollbar">
           <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 px-2">{t('guide.nav.title')}</p>
           
           <NavItem id="intro" label={t('guide.nav.intro')} icon={<BookOpen size={16}/>} active={activeSection} onClick={scrollTo} />
           <NavItem id="start" label={t('guide.nav.start')} icon={<Zap size={16}/>} active={activeSection} onClick={scrollTo} />
           <NavItem id="backtest" label={t('nav.backtest')} icon={<BarChart2 size={16}/>} active={activeSection} onClick={scrollTo} />
           <NavItem id="modes" label={t('guide.nav.modes')} icon={<Layers size={16}/>} active={activeSection} onClick={scrollTo} />
           <NavItem id="signals" label={t('guide.nav.signals')} icon={<Brain size={16}/>} active={activeSection} onClick={scrollTo} />
           <NavItem id="logic" label={t('guide.nav.logic')} icon={<Cpu size={16}/>} active={activeSection} onClick={scrollTo} />
           <NavItem id="risk" label={t('guide.nav.risk')} icon={<Shield size={16}/>} active={activeSection} onClick={scrollTo} />
           <NavItem id="profile" label={`${t('nav.cabinet')} & API`} icon={<User size={16}/>} active={activeSection} onClick={scrollTo} />
           <NavItem id="logs" label={t('guide.nav.logs')} icon={<Activity size={16}/>} active={activeSection} onClick={scrollTo} />
           <NavItem id="telegram" label={t('guide.nav.telegram')} icon={<Terminal size={16}/>} active={activeSection} onClick={scrollTo} />
           <NavItem id="trouble" label={t('guide.nav.trouble')} icon={<AlertTriangle size={16}/>} active={activeSection} onClick={scrollTo} />
           <NavItem id="faq" label={t('guide.nav.faq')} icon={<HelpCircle size={16}/>} active={activeSection} onClick={scrollTo} />
        </div>
      </div>

      {/* 2. КОНТЕНТ (ЦЕНТР) */}
      <div className="flex-1 max-w-5xl pb-32 overflow-y-auto pr-2 no-scrollbar">
        
        {/* ЗАГОЛОВОК */}
        <div className="mb-12 border-b border-slate-800 pb-8">
           <div className="flex items-center gap-3 mb-4">
             <div className="bg-blue-600 p-2 rounded-lg">
                <BookOpen className="text-white" size={32} />
             </div>
             <h1 className="text-4xl font-bold text-white">{t('guide.header.title')}</h1>
           </div>
           <p className="text-lg text-slate-400 leading-relaxed">
             {t('guide.header.desc')}
           </p>
        </div>

        {/* --- 1. ВВЕДЕНИЕ --- */}
        <section id="intro" className="mb-20 scroll-mt-6">
           <SectionHeader icon={<BookOpen className="text-blue-500"/>} title={t('guide.intro.title')} />
           <div className="bg-[#1e293b]/50 border border-slate-700/50 rounded-2xl p-8 text-slate-300 space-y-6">
             <p>{t('guide.intro.text')}</p>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FeatureBlock title="Multi-Exchange" desc={t('guide.intro.f1')} />
                <FeatureBlock title="Trend Guard™" desc={t('guide.intro.f2')} />
                <FeatureBlock title="Smart Entry" desc={t('guide.intro.f3')} />
                <FeatureBlock title={t('guide.intro.f4_t')} desc={t('guide.intro.f4_d')} />
             </div>
           </div>
        </section>

        {/* --- 2. БЫСТРЫЙ СТАРТ --- */}
        <section id="start" className="mb-20 scroll-mt-6">
           <SectionHeader icon={<Zap className="text-yellow-500"/>} title={t('guide.start.title')} />
           <div className="space-y-8">
              <Step number="01" title={t('guide.start.s1_t')}>
                 <div className="bg-[#0f172a] p-6 rounded-xl border border-slate-800">
                    <ul className="space-y-3 text-sm">
                       <li className="flex items-center gap-3 text-slate-300"><CheckCircle2 size={16} className="text-green-500 shrink-0" /> {t('guide.start.s1_l1')}</li>
                       <li className="flex items-center gap-3 text-slate-300"><CheckCircle2 size={16} className="text-green-500 shrink-0" /> {t('guide.start.s1_l2')}</li>
                       <li className="flex items-center gap-3 text-slate-300"><XCircle size={16} className="text-red-500 shrink-0" /> {t('guide.start.s1_l3')}</li>
                    </ul>
                 </div>
              </Step>
              <Step number="02" title={t('guide.start.s2_t')}><p className="text-slate-400 ml-14 mb-2">{t('guide.start.s2_d')}</p></Step>
              <Step number="03" title={t('guide.start.s3_t')}><p className="text-slate-400 ml-14">{t('guide.start.s3_d')}</p></Step>
           </div>
        </section>

        {/* --- NEW: BACKTESTER (Переведено) --- */}
        <section id="backtest" className="mb-20 scroll-mt-6">
           <SectionHeader icon={<BarChart2 className="text-indigo-500"/>} title={t('backtest.title')} />
           <div className="bg-[#1e293b] rounded-2xl p-6 border border-slate-800 shadow-xl">
             <p className="text-slate-400 mb-6">{t('backtest.subtitle')}</p>
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-900/50 p-5 rounded-xl border border-indigo-500/20">
                   <h4 className="text-indigo-400 font-bold mb-2">1. {t('backtest.info1Title')}</h4>
                   <p className="text-xs text-slate-400">{t('backtest.info1Desc')}</p>
                </div>
                <div className="bg-slate-900/50 p-5 rounded-xl border border-indigo-500/20">
                   <h4 className="text-indigo-400 font-bold mb-2">2. {t('backtest.info3Title')}</h4>
                   <p className="text-xs text-slate-400">{t('backtest.info3Desc')}</p>
                </div>
             </div>
           </div>
        </section>

        {/* --- 3. РЕЖИМЫ СТРАТЕГИИ --- */}
        <section id="modes" className="mb-20 scroll-mt-6">
           <SectionHeader icon={<Layers className="text-pink-500"/>} title={t('guide.modes.title')} />
           <div className="bg-[#1e293b] rounded-2xl p-6 border border-slate-800 shadow-xl relative overflow-hidden">
             <div className="absolute top-0 right-0 w-64 h-64 bg-pink-500/10 blur-3xl rounded-full pointer-events-none"></div>
             <p className="text-slate-400 mb-6">{t('guide.modes.desc')}</p>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
               <div className="bg-slate-900/80 p-5 rounded-xl border border-purple-500/30">
                 <div className="text-purple-400 font-bold mb-3 flex items-center gap-2 text-sm uppercase tracking-wider"><div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></div>♻️ Auto Mode</div>
                 <p className="text-xs text-slate-400 leading-relaxed">{t('guide.modes.auto')}</p>
               </div>
               <div className="bg-slate-900/80 p-5 rounded-xl border border-blue-500/30">
                 <div className="text-blue-400 font-bold mb-3 flex items-center gap-2 text-sm uppercase tracking-wider"><div className="w-2 h-2 rounded-full bg-blue-500"></div>🛡️ Trend Mode</div>
                 <p className="text-xs text-slate-400 leading-relaxed">{t('guide.modes.trend')}</p>
               </div>
               <div className="bg-slate-900/80 p-5 rounded-xl border border-orange-500/30">
                 <div className="text-orange-400 font-bold mb-3 flex items-center gap-2 text-sm uppercase tracking-wider"><div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>🐇 Scalp Mode</div>
                 <p className="text-xs text-slate-400 leading-relaxed">{t('guide.modes.scalp')}</p>
               </div>
             </div>
           </div>
        </section>

        {/* --- 4. ТИПЫ СИГНАЛОВ V2 --- */}
        <section id="signals" className="mb-20 scroll-mt-6">
           <SectionHeader icon={<Brain className="text-emerald-500"/>} title={t('guide.signals.title')} />
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-[#1e293b] p-6 rounded-2xl border border-slate-700">
                 <h3 className="text-white font-bold mb-4 flex items-center gap-2"><BarChart2 className="text-emerald-400"/> Smart Entry</h3>
                 <div className="space-y-3">
                    <div className="bg-emerald-900/20 p-3 rounded-lg border border-emerald-500/20">
                       <span className="text-emerald-400 font-bold text-xs uppercase block mb-1">🧠 SMART LONG</span>
                       <p className="text-xs text-slate-400">{t('guide.signals.s_long')}</p>
                    </div>
                    <div className="bg-rose-900/20 p-3 rounded-lg border border-rose-500/20">
                       <span className="text-rose-400 font-bold text-xs uppercase block mb-1">🧠 SMART SHORT</span>
                       <p className="text-xs text-slate-400">{t('guide.signals.s_short')}</p>
                    </div>
                 </div>
              </div>
              <div className="bg-[#1e293b] p-6 rounded-2xl border border-slate-700">
                 <h3 className="text-white font-bold mb-4 flex items-center gap-2"><Activity className="text-blue-400"/> Standard Entry</h3>
                 <div className="space-y-3">
                    <div className="bg-blue-900/20 p-3 rounded-lg border border-blue-500/20">
                       <span className="text-blue-400 font-bold text-xs uppercase block mb-1">🟢 TREND ENTRY</span>
                       <p className="text-xs text-slate-400">{t('guide.signals.trend')}</p>
                    </div>
                    <div className="bg-orange-900/20 p-3 rounded-lg border border-orange-500/20">
                       <span className="text-orange-400 font-bold text-xs uppercase block mb-1">🚀 SCALP ENTRY</span>
                       <p className="text-xs text-slate-400">{t('guide.signals.scalp')}</p>
                    </div>
                 </div>
              </div>
           </div>
        </section>

        {/* --- 5. АЛГОРИТМ --- */}
        <section id="logic" className="mb-20 scroll-mt-6">
           <SectionHeader icon={<Cpu className="text-purple-500"/>} title={t('guide.algo.title')} />
           <div className="bg-[#1e293b] rounded-2xl overflow-hidden border border-slate-800">
             <div className="divide-y divide-slate-800">
                 <LogicRow step={t('guide.algo.s1_t')} desc={t('guide.algo.s1_d')} />
                 <LogicRow step={t('guide.algo.s2_t')} desc={t('guide.algo.s2_d')} />
                 <LogicRow step={t('guide.algo.s3_t')} desc={t('guide.algo.s3_d')} />
                 <LogicRow step={t('guide.algo.s4_t')} desc={t('guide.algo.s4_d')} />
                 <LogicRow step={t('guide.algo.s5_t')} desc={t('guide.algo.s5_d')} />
             </div>
           </div>
        </section>

        {/* --- 6. РИСК-МЕНЕДЖМЕНТ --- */}
        <section id="risk" className="mb-20 scroll-mt-6">
           <SectionHeader icon={<Shield className="text-red-500"/>} title={t('guide.risk.title')} />
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-[#1e293b] p-6 rounded-2xl border border-slate-800">
                 <h3 className="font-bold text-white mb-4 flex items-center gap-2"><BarChart3 className="text-blue-500"/> {t('guide.risk.calc_title')}</h3>
                 <div className="bg-black/30 p-4 rounded-lg font-mono text-xs text-slate-300 mb-4">PositionSize = (Balance * Risk%) / DistanceToStop</div>
                 <p className="text-sm text-slate-400 leading-relaxed">{t('guide.risk.calc_desc')}</p>
              </div>
              <div className="space-y-4">
                 <RiskDetail title="Paper Logic" value="Futures PnL">
                    {t('guide.risk.paper_desc')}
                 </RiskDetail>
                 <RiskDetail title="Smart Leverage" value="Auto">{t('guide.risk.lev_desc')}</RiskDetail>
                 <RiskDetail title="Trailing Stop" value="Active">{t('guide.risk.trail_desc')}</RiskDetail>
              </div>
           </div>
        </section>

        {/* --- NEW: PROFILE & API (Переведено) --- */}
        <section id="profile" className="mb-20 scroll-mt-6">
           <SectionHeader icon={<Settings className="text-cyan-500"/>} title={`${t('nav.cabinet')} & API`} />
           <div className="bg-[#1e293b] p-6 rounded-2xl border border-slate-800">
              <div className="flex gap-4 items-start mb-6">
                 <div className="bg-cyan-500/20 p-3 rounded-lg"><Zap className="text-cyan-400" size={24}/></div>
                 <div>
                    <h3 className="text-white font-bold text-lg">{t('guide.start.s2_t')}</h3> {/* Connection */}
                    <p className="text-slate-400 text-sm mt-1">{t('guide.start.s2_d')}</p>
                 </div>
              </div>
              <div className="bg-orange-500/10 p-4 rounded-xl border border-orange-500/20">
                 <h4 className="text-orange-400 font-bold text-xs uppercase flex items-center gap-2 mb-2"><AlertTriangle size={14}/> {t('guide.trouble.title')}</h4>
                 <p className="text-xs text-slate-300">{t('guide.start.s1_l3')}</p> {/* Disable Withdrawal */}
              </div>
           </div>
        </section>

        {/* --- 7. ЛОГИ --- */}
        <section id="logs" className="mb-20 scroll-mt-6">
           <SectionHeader icon={<Activity className="text-green-500"/>} title={t('guide.logs.title')} />
           <div className="space-y-3">
              <LogExample type="info" text="🚀 ENTRY: SMART_LONG ETH/USDT" desc={t('guide.logs.l1')} />
              <LogExample type="warn" text="⛔️ Trend Filter: ETH (Price < EMA200). LONG forbidden." desc={t('guide.logs.l2')} />
              <LogExample type="info" text="[Settings] 💱 Exchange switched to: BYBIT" desc={t('guide.logs.l3')} />
              <LogExample type="success" text="🛡️ DOGE: Moved to Breakeven." desc={t('guide.logs.l4')} />
           </div>
        </section>

        {/* --- 8. ТЕЛЕГРАМ --- */}
        <section id="telegram" className="mb-20 scroll-mt-6">
           <SectionHeader icon={<Terminal className="text-blue-400"/>} title={t('guide.tg.title')} />
           <div className="bg-[#1e293b] rounded-xl overflow-hidden border border-slate-800">
              <table className="w-full text-left text-sm text-slate-400">
                 <thead className="bg-slate-900 text-slate-200 uppercase font-bold">
                    <tr><th className="p-4">{t('guide.tg.cmd')}</th><th className="p-4">{t('guide.tg.desc')}</th></tr>
                 </thead>
                 <tbody className="divide-y divide-slate-800">
                    <tr><td className="p-4 font-mono text-blue-400">/start</td><td className="p-4">{t('guide.tg.c1')}</td></tr>
                    <tr><td className="p-4 font-mono text-blue-400">/login [email]</td><td className="p-4">{t('guide.tg.c2')}</td></tr>
                    <tr><td className="p-4 font-mono text-blue-400">/status</td><td className="p-4">{t('guide.tg.c3')}</td></tr>
                    <tr><td className="p-4 font-mono text-blue-400">/stop</td><td className="p-4">{t('guide.tg.c4')}</td></tr>
                 </tbody>
              </table>
           </div>
        </section>

        {/* --- 9. РЕШЕНИЕ ПРОБЛЕМ --- */}
        <section id="trouble" className="mb-20 scroll-mt-6">
           <SectionHeader icon={<AlertTriangle className="text-orange-500"/>} title={t('guide.trouble.title')} />
           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ProblemCard title={t('guide.trouble.p1_t')} desc={t('guide.trouble.p1_d')} />
              <ProblemCard title={t('guide.trouble.p2_t')} desc={t('guide.trouble.p2_d')} />
              <ProblemCard title={t('guide.trouble.p3_t')} desc={t('guide.trouble.p3_d')} />
              <ProblemCard title={t('guide.trouble.p4_t')} desc={t('guide.trouble.p4_d')} />
              <ProblemCard title={t('guide.trouble.p5_t')} desc={t('guide.trouble.p5_d')} />
              <ProblemCard title={t('guide.trouble.p6_t')} desc={t('guide.trouble.p6_d')} />
           </div>
        </section>

        {/* --- 10. FAQ --- */}
        <section id="faq" className="mb-10 scroll-mt-6">
           <div className="bg-gradient-to-r from-blue-600/10 to-purple-600/10 border border-blue-500/20 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-2">{t('guide.support.title')}</h3>
              <p className="text-slate-400 mb-6">{t('guide.support.desc')}</p>
              <button className="bg-white text-slate-900 hover:bg-blue-50 font-bold py-3 px-8 rounded-xl transition-all shadow-lg flex items-center gap-2 mx-auto">
                 <HeartHandshake size={20} /> {t('guide.support.btn')}
              </button>
           </div>
        </section>

      </div>
    </div>
  );
};

// --- SUB COMPONENTS ---
const SectionHeader = ({ icon, title }: any) => <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3 border-b border-slate-800 pb-4">{icon} {title}</h2>;
const NavItem = ({ id, label, icon, active, onClick }: any) => (
  <button onClick={() => onClick(id)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all mb-1 ${active === id ? 'bg-blue-600/10 text-blue-400 border border-blue-600/20 shadow-lg shadow-blue-900/20' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}>
    {icon} {label} {active === id && <ChevronRight size={14} className="ml-auto opacity-50" />}
  </button>
);
const FeatureBlock = ({ title, desc }: any) => <div className="bg-slate-800/30 p-5 rounded-xl border border-slate-700/50 hover:bg-slate-800/50 transition-colors"><h4 className="font-bold text-blue-400 mb-2 text-lg">{title}</h4><p className="text-sm text-slate-400 leading-relaxed">{desc}</p></div>;
const Step = ({ number, title, children }: any) => <div className="flex gap-6 relative"><div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center font-bold text-blue-400 text-lg z-10">{number}</div><div className="absolute left-5 top-10 bottom-[-32px] w-px bg-slate-800 -z-0 last:hidden"></div><div className="flex-1 pb-8"><h3 className="text-xl font-bold text-white mb-3">{title}</h3><div className="text-sm leading-relaxed">{children}</div></div></div>;
const LogicRow = ({ step, desc }: any) => <div className="p-6 hover:bg-white/5 transition-colors"><p className="font-bold text-white mb-1">{step}</p><div className="text-sm text-slate-400">{desc}</div></div>;
const RiskDetail = ({ title, value, children }: any) => <div className="bg-[#1e293b]/50 p-4 rounded-xl border border-slate-800"><div className="flex justify-between items-center mb-2"><span className="text-sm font-bold text-slate-300">{title}</span><span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">{value}</span></div><p className="text-xs text-slate-500">{children}</p></div>;
const LogExample = ({ type, text, desc }: any) => { const colors = { info: 'text-blue-400 border-blue-500/20 bg-blue-500/5', warn: 'text-orange-400 border-orange-500/20 bg-orange-500/5', success: 'text-green-400 border-green-500/20 bg-green-500/5', error: 'text-red-400 border-red-500/20 bg-red-500/5' }; return <div className={`p-3 rounded-lg border flex items-center gap-4 ${colors[type as keyof typeof colors]}`}><div className="font-mono text-xs shrink-0 w-1/3">{text}</div><div className="text-xs text-slate-400 border-l border-slate-700 pl-4">{desc}</div></div>; };
const ProblemCard = ({ title, desc }: any) => <div className="bg-[#1e293b] p-5 rounded-xl border border-slate-800"><h4 className="font-bold text-white mb-2 flex items-center gap-2"><XCircle size={16} className="text-red-500"/> {title}</h4><p className="text-xs text-slate-400 leading-relaxed">{desc}</p></div>;